import { 
  Server, 
  Database, 
  Shield, 
  Globe, 
  Cpu, 
  Cloud,
  Lock,
  RefreshCw,
  CheckCircle2
} from "lucide-react";

export default function TechArchitecture() {
  const techStack = [
    { icon: Globe, label: "React.js", sublabel: "Modern Frontend" },
    { icon: Server, label: "FastAPI", sublabel: "High-Performance API" },
    { icon: Database, label: "MongoDB", sublabel: "Scalable Database" },
    { icon: Cpu, label: "OpenAI GPT", sublabel: "AI Engine" },
    { icon: Lock, label: "JWT + SSO", sublabel: "Secure Auth" },
    { icon: Cloud, label: "Cloud Native", sublabel: "100% SaaS" },
  ];

  const reliabilityFeatures = [
    { icon: CheckCircle2, label: "99.9% Uptime SLA" },
    { icon: RefreshCw, label: "Multi-zone Replication" },
    { icon: Shield, label: "SOC 2 Type II Compliant" },
    { icon: Database, label: "Daily Automated Backups" },
  ];

  return (
    <section id="platform" className="section-dark section-padding" data-testid="tech-section">
      <div className="container-main">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <span className="text-sm font-mono uppercase tracking-widest text-blue-400 mb-4 block">
              Platform
            </span>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
              Enterprise-grade reliability, built for scale
            </h2>
            <p className="text-lg text-slate-400 leading-relaxed mb-8">
              Modern architecture designed for performance, security, and seamless scalability. 
              Your data is protected with geographic redundancy and industry-leading security practices.
            </p>

            {/* Reliability Features */}
            <div className="grid grid-cols-2 gap-4">
              {reliabilityFeatures.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <feature.icon size={20} className="text-green-400" />
                  <span className="text-slate-300 font-medium">{feature.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Tech Stack Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {techStack.map((tech, index) => (
              <div 
                key={index}
                className="tech-card group hover:border-slate-400 transition-colors"
              >
                <div className="tech-card-icon group-hover:bg-blue-600/20 transition-colors">
                  <tech.icon size={20} className="text-blue-400" />
                </div>
                <div className="text-white font-semibold">{tech.label}</div>
                <div className="text-sm text-slate-500">{tech.sublabel}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Stats */}
        <div className="mt-20 pt-16 border-t border-slate-700/50">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "<4h", label: "Recovery Time Objective" },
              { value: "<1h", label: "Recovery Point Objective" },
              { value: "30 days", label: "Backup Retention" },
              { value: "3", label: "Isolated Environments" },
            ].map((stat, index) => (
              <div key={index}>
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-sm text-slate-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Integration Partners */}
        <div className="mt-16 pt-16 border-t border-slate-700/50">
          <p className="text-center text-sm text-slate-500 mb-8">Native Integrations</p>
          <div className="flex flex-wrap justify-center gap-8 text-slate-500">
            {["Microsoft Teams", "Google Workspace", "Active Directory", "Intune", "Jamf", "SCCM"].map((partner, index) => (
              <span key={index} className="text-sm font-medium hover:text-white transition-colors cursor-default">
                {partner}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
